'use client';

import clsx from 'clsx';

import { CompanySelectField } from '@/features/contacts/companies/components/CompanySelectField';
import { PersonSelectField } from '@/features/contacts/people/components/PersonSelectField';

import styles from './CompanyPersonSelectFields.module.scss';

import type { CompanyOption } from '@/features/contacts/companies/components/CompanySelectField';
import type { PersonOption } from '@/features/contacts/people/components/PersonSelectField';

type CompanyPersonSelectFieldsProps = {
  company: CompanyOption | null;
  person: PersonOption | null;
  companyError?: string;
  personError?: string;
  companyLabel?: string;
  personLabel?: string;
  companyPlaceholder?: string;
  personPlaceholder?: string;
  disabled?: boolean;
  layout?: 'inline' | 'stack';
  onCompanyChange: (
    company: CompanyOption | null,
    nextPerson: PersonOption | null,
  ) => void;
  onPersonChange: (
    person: PersonOption | null,
    nextCompany: CompanyOption | null,
  ) => void;
};

function resolveCompanyFromPerson(
  person: PersonOption | null,
  currentCompany: CompanyOption | null,
): CompanyOption | null {
  if (!person?.companyId) {
    return currentCompany;
  }

  if (currentCompany?.id === person.companyId) {
    return currentCompany;
  }

  return {
    id: person.companyId,
    name: person.companyName || currentCompany?.name || 'Selected company',
  };
}

export function CompanyPersonSelectFields({
  company,
  person,
  companyError,
  personError,
  companyLabel = 'Company',
  personLabel = 'Person',
  companyPlaceholder = 'Search companies...',
  personPlaceholder = 'Optional person',
  disabled = false,
  layout = 'inline',
  onCompanyChange,
  onPersonChange,
}: CompanyPersonSelectFieldsProps) {
  const className = clsx(
    styles.companyPersonSelectFields,
    layout === 'stack'
      ? styles.companyPersonSelectFieldsStack
      : styles.companyPersonSelectFieldsInline,
  );

  return (
    <div className={className}>
      <CompanySelectField
        value={company}
        onChange={(nextCompany) => {
          const nextPerson =
            nextCompany &&
            person?.companyId &&
            person.companyId !== nextCompany.id
              ? null
              : person;

          onCompanyChange(nextCompany, nextPerson);
        }}
        error={companyError}
        label={companyLabel}
        placeholder={companyPlaceholder}
        disabled={disabled}
      />

      <PersonSelectField
        value={person}
        companyId={company?.id ?? null}
        onChange={(nextPerson) => {
          const nextCompany = resolveCompanyFromPerson(nextPerson, company);
          onPersonChange(nextPerson, nextCompany);
        }}
        error={personError}
        label={personLabel}
        placeholder={personPlaceholder}
        disabled={disabled}
      />
    </div>
  );
}
